import {z} from "zod";
import {offerTemplateValidationMessages} from "../../../messages/offerTemplates.messages";
import {
  OfferBindingValue,
  OfferCatalogBlock,
  OfferChapter,
  OfferLiteralValue,
  OfferRichTextBlock,
  OfferRichTextContent,
  OfferSupplierSelection,
  OfferVariable,
  UpdateOfferTemplateBody,
} from "@project/sdk/sales/zod";

const variable = OfferVariable.strict().extend({
  value: z.union([OfferLiteralValue.strict(), OfferBindingValue.strict(), z.null()]),
});
const richText = OfferRichTextContent.strict().extend({variables: z.array(variable)});
const block = z.union([
  OfferRichTextBlock.strict().extend({content: richText}),
  OfferCatalogBlock.strict().extend({
    selectedSupplierOffers: z.array(OfferSupplierSelection.strict()),
  }),
]);
const chapter = OfferChapter.strict().extend({
  applyCommission: OfferChapter.shape.applyCommission.unwrap(),
  blocks: z.array(block),
});
const fields = UpdateOfferTemplateBody.strict().extend({
  content: UpdateOfferTemplateBody.shape.content.strict().extend({
    header: richText,
    footer: richText,
    chapters: z.array(chapter),
  }),
});

export const updateOfferTemplateFormSchema = fields.superRefine((payload, ctx) => {
  const duplicate = (values: string[], prefix: (string | number)[], field: string) => {
    const seen = new Set<string>();
    values.forEach((value, index) => {
      if (seen.has(value))
        ctx.addIssue({
          code: "custom",
          path: [...prefix, index, field],
          message: offerTemplateValidationMessages.duplicateIdentity.id,
          params: {descriptor: offerTemplateValidationMessages.duplicateIdentity},
        });
      seen.add(value);
    });
  };
  const variables = (scope: z.output<typeof richText>, prefix: (string | number)[]) =>
    duplicate(
      scope.variables.map((item) => item.name),
      [...prefix, "variables"],
      "name",
    );
  duplicate(
    payload.content.chapters.map((item) => item.chapterId.toLowerCase()),
    ["content", "chapters"],
    "chapterId",
  );
  variables(payload.content.header, ["content", "header"]);
  variables(payload.content.footer, ["content", "footer"]);
  payload.content.chapters.forEach((item, index) => {
    duplicate(
      item.blocks.map((entry) => entry.blockId.toLowerCase()),
      ["content", "chapters", index, "blocks"],
      "blockId",
    );
    item.blocks.forEach((entry, blockIndex) => {
      if (entry.blockType === "RICH_TEXT")
        variables(entry.content, ["content", "chapters", index, "blocks", blockIndex, "content"]);
    });
  });
});
export type UpdateOfferTemplateFormPayload = z.output<typeof updateOfferTemplateFormSchema>;
export const draftUpdateOfferTemplateFormSchema = fields.partial();
export type DraftUpdateOfferTemplateFormPayload = z.output<
  typeof draftUpdateOfferTemplateFormSchema
>;
